import type { Inventory } from '../gameplay/inventory';
import { getItem } from '../gameplay/inventory';
import type { ItemId } from '../gameplay/inventory';

interface ActiveToast {
  readonly itemId: ItemId;
  readonly element: HTMLElement;
  amount: number;
  timeoutId: number;
}

const TOAST_LIFETIME_MS = 1800;
const TOAST_FADE_MS = 280;
const MAX_TOASTS = 4;

/**
 * Discreet "+N item" floater that pulses in the corner whenever the player
 * gains inventory items. Subscribes to one or more Inventories, diffs
 * snapshots, and only shows toasts for net-positive deltas — so dragging
 * stacks around inside the inventory or selling at a shop never spawns a
 * toast.
 *
 * Repeated pickups of the same item within the lifetime window stack into
 * the existing toast (incrementing its number and resetting its timer)
 * instead of producing a vertical wall of duplicates.
 */
export class PickupToastLayer {
  readonly element: HTMLElement;
  private readonly inventories: readonly Inventory[];
  private readonly counts = new Map<ItemId, number>();
  private readonly active: ActiveToast[] = [];
  private readonly subscriptions: Array<() => void> = [];

  constructor(inventories: readonly Inventory[]) {
    this.element = document.createElement('div');
    this.element.className = 'pickup-toast-layer';
    this.inventories = inventories;

    // Seed totals from the current state BEFORE subscribing so the
    // immediate first-snapshot callback computes zero deltas instead of
    // dumping the player's whole inventory as a column of toasts.
    for (const [itemId, count] of this.totalsByItem()) {
      this.counts.set(itemId, count);
    }

    for (const inv of inventories) {
      this.subscriptions.push(inv.subscribe(() => this.handle()));
    }
  }

  dispose(): void {
    for (const unsub of this.subscriptions) unsub();
    for (const toast of this.active) {
      window.clearTimeout(toast.timeoutId);
    }
    this.active.length = 0;
    this.element.remove();
  }

  private totalsByItem(): Map<ItemId, number> {
    const totals = new Map<ItemId, number>();
    for (const inv of this.inventories) {
      for (const slot of inv.snapshot()) {
        if (!slot) continue;
        totals.set(slot.itemId, (totals.get(slot.itemId) ?? 0) + slot.count);
      }
    }
    return totals;
  }

  private handle(): void {
    const totals = this.totalsByItem();
    for (const [itemId, count] of totals) {
      const previous = this.counts.get(itemId) ?? 0;
      const delta = count - previous;
      if (delta > 0) this.showToast(itemId, delta);
    }
    this.counts.clear();
    for (const [itemId, count] of totals) this.counts.set(itemId, count);
  }

  private showToast(itemId: ItemId, amount: number): void {
    const existing = this.active.find((t) => t.itemId === itemId);
    if (existing) {
      existing.amount += amount;
      const amountEl = existing.element.querySelector('.pickup-toast-amount');
      if (amountEl) amountEl.textContent = `+${existing.amount}`;
      window.clearTimeout(existing.timeoutId);
      existing.timeoutId = window.setTimeout(
        () => this.fade(existing),
        TOAST_LIFETIME_MS
      );
      // Pop pulse on update so a stacking pickup is still visible.
      existing.element.classList.remove('is-pulse');
      void existing.element.offsetWidth;
      existing.element.classList.add('is-pulse');
      return;
    }

    const item = getItem(itemId);
    const el = document.createElement('div');
    el.className = 'pickup-toast';
    el.style.setProperty('--toast-accent', item.accent);
    el.style.setProperty('--toast-glow', item.accentGlow);

    const amountSpan = document.createElement('span');
    amountSpan.className = 'pickup-toast-amount';
    amountSpan.textContent = `+${amount}`;

    const nameSpan = document.createElement('span');
    nameSpan.className = 'pickup-toast-name';
    nameSpan.textContent = item.shortName ?? item.name;

    el.append(amountSpan, nameSpan);
    this.element.append(el);

    // Trigger entrance animation on next frame so the transition runs.
    requestAnimationFrame(() => el.classList.add('is-visible'));

    const toast: ActiveToast = {
      itemId,
      element: el,
      amount,
      timeoutId: 0
    };
    toast.timeoutId = window.setTimeout(
      () => this.fade(toast),
      TOAST_LIFETIME_MS
    );
    this.active.push(toast);

    // Cap on-screen toasts so a flood of pickups doesn't fill the corner.
    while (this.active.length > MAX_TOASTS) {
      const oldest = this.active.shift();
      if (oldest) {
        window.clearTimeout(oldest.timeoutId);
        this.remove(oldest);
      }
    }
  }

  private fade(toast: ActiveToast): void {
    const idx = this.active.indexOf(toast);
    if (idx < 0) return;
    this.active.splice(idx, 1);
    toast.element.classList.add('is-fading');
    window.setTimeout(() => this.remove(toast), TOAST_FADE_MS);
  }

  private remove(toast: ActiveToast): void {
    if (toast.element.parentElement) {
      toast.element.parentElement.removeChild(toast.element);
    }
  }
}
